import * as vis from 'vis';
import * as _ from 'lodash';

// config
import { appConfig } from '../../config/app-config';

// core
import { UiMessageSnackService } from '../../core/components/message-snack/service/message-snack.service';

// own feature
import { navigationConfig } from '../config/navigation.config';
import { NavElementsRoot } from './nav-elements-root';
import { NavElementsEdgeGroup, INavElementsEdge } from './nav-elements-edgegroup';
import { NavElementsNodeGroup, INavElementsNode } from './nav-elements-nodegroup';
import { INavGraphDisplayOptionEdges } from './nav-display-edgegroup-pairs';
import { INavDisplayOptions } from './nav-display-options.interface';
import { NavVisOptions } from './nav-vis-options';
import {
    EdgeDirectionBKWD, EdgeDirectionFWD, NavListNodeType_root,
    NavListNodeType_edgeLevel1, NavListNodeType_nodeLevel1,
    NavListNodeType_edgeLevel2, NavListNodeType_nodeLevel2, ITreeNode, ITreeEdge
} from './nav.types';
import { INavigationState, INavConfig, IEdgeDefinition } from '../store/store';
import { IDbDoc } from '../service/tree-data-service.service';
import { ConfirmDialogsService } from '../../core/components/confirm-dialog/service/confirm-dialog.service';

const SVG_PREFIX = 'data:image/svg+xml;charset=utf-8,';

export class NavElementsContainer {

    // Collections
    private _root: NavElementsRoot = null;
    private _virtualRoot: NavElementsRoot = null;
    private _rootToExtend: NavElementsRoot = null;
    private _nodeGroups: NavElementsNodeGroup[] = [];
    private _edgeGroups: NavElementsEdgeGroup[] = [];
    private _nodeDepthMap: Map<string, number> = null;

    private graphData: IGraphData;
    private currentDisplayOpts: INavDisplayOptions;
    private config: INavConfig;
    private canvas: HTMLCanvasElement;

    private komponentenMap: Map<string, {
        typ: string,
        anlage: string,
        bezeichnung: string,
    }> = new Map();

    // ctor
    constructor(
        private _messageSnackService: UiMessageSnackService = null
    ) {

    }

    // ---------------------------------------------------------------------------------------------
    // -- Public -----------------------------------------------------------------------------------
    // ---------------------------------------------------------------------------------------------

    /**
    * Bestimmt, ob Daten zur Visualisierung vorliegen.
    *
    * @param keinen
    * @returns [Boolean] True, wenn Daten vorliegen.
    */
    public getVirtualRoot(): NavElementsRoot {
        return this._virtualRoot;
    }

    /**
    * Gibt nodeID des virtuellen Root zurück.
    *
    * @param keinen
    * @returns [string] ID des virtuellen Node.
    */
    public getVirtualRootId(): string {
        if (!this._virtualRoot) {
            console.warn('[NavElementsContainer]:getVirtualRootId() - Kein virtueller Node!');
            return;
        }
        return this._virtualRoot.rootObj.id;
    }

    /**
    * Bestimmt, ob Daten zur Visualisierung vorliegen.
    *
    * @param keinen
    * @returns [Boolean] True, wenn Daten vorliegen.
    */
    public rootInitialized(): boolean {
        return !!this._virtualRoot;
    }

    /**
     * Definiert die ID das virtual_root Object.
     *
     * @param [root: NavSearchRoot] Object vom Type NavSearchRoot
     * @returns void
     */
    public setRootToExtend(nodeId: string): void {
        this._rootToExtend = new NavElementsRoot(
            {
                id: nodeId,
                group: '',
                label: '',
                titel: ''
            }
        );
    }

    /**
    * Merge der verfügbaren Edge-Gruppen in die displayOptions.
    *
    * @param arrToFill: INavGraphDisplayOptionEdges
    * @returns void.
    */
    public fillDisplayOptionEdges(arrToFill: INavGraphDisplayOptionEdges[]): void {

        try {
            // set all to false
            arrToFill.forEach((arrItem: INavGraphDisplayOptionEdges) => {
                arrItem.existInCont = false;
            });

            // all edge groups in container
            this._edgeGroups.forEach((item: NavElementsEdgeGroup) => {
                // all display options
                arrToFill.forEach((arrItem: INavGraphDisplayOptionEdges) => {
                    if (arrItem.value === item.name) {
                        arrItem.existInCont = true;
                    }
                });
            });
        } catch (err) {
            console.warn('Anomalie beim Füllen der Graph Display Options');
        }
    }

    /**
     * Nimmt den SearchResult aus dem Parameter, parsed den Inhalt und legt das Modell im Container an.
     * Falls in der Config-Datei (navigationConfig.navSearchParse.debugMode) true ist, wird
     * noch der Inhalt auf die Console geschrieben.
     * Tritt ein Fehler auf, wird über den MessageSnackService eine Nachricht abgesetzt.
     *
     * @param [searchResult: any] Object mit Daten des searchResult.
     * @returns void
     */
    public computeInitialSearchResult(
        searchResult: any,
        config: INavConfig,
        displayOptions?: any
    ): void {
        try {
            // reset data
            this._root = null;
            this._virtualRoot = null;
            this._rootToExtend = null;
            this._nodeGroups = [];
            this._edgeGroups = [];

            // parse
            if (!this.parseSearchResult(searchResult, config)) {
                this._messageSnackService.showSnackError(
                    'Fehler beim analysieren des SearchResults',
                    'NavSearchContainer');
                console.warn('Fehler beim analysieren des SearchResults');
                return;
            }
            if (navigationConfig.debugModeNavSearchParse) {
                this.dumpContent();
            }
        } catch (err) {
            if (this._messageSnackService) {
                this._messageSnackService.showSnackError(
                    'Fehler beim analysieren des SearchResults',
                    'NavSearchContainer');
                console.warn(err);
            } else {
                console.warn(err);
            }
        }
    }

    /**
     * Nimmt den SearchResult aus dem Parameter, parsed den Inhalt und erweitert das Modell im Container.
     * Falls in der Config-Datei (navigationConfig.navSearchParse.debugMode) true ist, wird
     * noch der Inhalt auf die Console geschrieben.
     * Tritt ein Fehler auf, wird über den MessageSnackService eine Nachricht abgesetzt.
     *
     * @param [searchResult: any] Object mit Daten des searchResult.
     * @returns void
     */
    public computeExtendedSearchResult(searchResult: any, config: INavConfig): void {
        try {
            if (!this.parseSearchResult(searchResult, config)) {
                this._messageSnackService.showSnackError(
                    'Fehler beim analysieren des SearchResults',
                    'NavSearchContainer');
                console.warn('Fehler beim analysieren des SearchResults');
                return;
            }
            if (navigationConfig.debugModeNavSearchParse) {
                this.dumpContent();
            }
        } catch (err) {
            if (this._messageSnackService) {
                this._messageSnackService.showSnackError(
                    'Fehler beim analysieren des SearchResults',
                    'NavSearchContainer');
                console.warn(err);
            } else {
                console.warn(err);
            }
        }
    }

    /**
     * Redefiniert das virtuelle Root Object neu.
     *
     * @param [nodeID: string] Node ID des zu setzenden virtuellen Knoten.
     * @returns void
     */
    public resetVirtualRoot(nodeID: string): void {
        const node = this.findNodeWithID(nodeID);
        if (node) {
            // set virtual root object
            this.setVirtualRoot(
                {
                    id: node.id,
                    group: node.group,
                    label: node.label,
                    titel: node.titel,
                    display: node.display,
                    anlage: node.anlage,
                }
            );
        } else {
            console.warn('Virtual Root konnte nicht definiert werden');
        }
    }

    // ---------------------------------------------------------------------------------------------
    // -- generate Vis Input
    // ---------------------------------------------------------------------------------------------

    public generateVisData(navState: INavigationState, options: INavDisplayOptions) {
        this.currentDisplayOpts = options;
        this.config = navState.config;
        this.graphData = {
            nodes: this.generateVisNodes(navState.config, options),
            edges: this.generateVisEdges(navState.config, options),
        };
        return this.graphData;
    }

    /**
     * Generiert Node Array für die Übergabe an vis.
     *
     * @param displayOptions Settings für die Visualisierung von Nodes.
     * @returns vis.DataSet<vis.Node>
     */
    public generateVisNodes(config: INavConfig, displayOptions?: INavDisplayOptions): vis.DataSet<vis.Node & { title?: string }> {

        // check
        if (!this._virtualRoot || !this._nodeGroups) {
            console.warn('Generierung der Nodes zur Visualisierung fehlgeschlagen: Keine Daten.');
            return null;
        }

        // initialize data
        const nodes = new Array<vis.Node & { title?: string }>();

        try {
            // nodeGroups
            for (const group of this._nodeGroups) {
                for (const child of group._children) {
                    if (!child.display) {
                        continue;
                    }

                    nodes.push(this.generateVisNode(child));
                }
            }
        } catch (err) {
            console.warn(err);
            if (this._messageSnackService) {
                this._messageSnackService.showSnackError(
                    'Fehler beim generieren der Elemente für den Graphen',
                    'NavSearchContainer');
            }
        }

        return new vis.DataSet(nodes);
    }

    private generateVisNode(node: INavElementsNode) {
        let title = '';
        const de = this.config.dictionary.find(entry => entry.key === node.group);
        if (de) {
            title = de.short;
        }

        // Padding, da bei Kreisen die Zentrierung nicht richtig funktioniert
        title = title ? title + (node.typ ? ' - ' + node.typ : '') + '\n' : '';
        if (title && title.length < 4) {
            while (title.length < 4) {
                title = ' ' + title + ' ';
            }
        }
        const label = (title || '') + `${node.label}`;
        if (node.anlage) {
            this.komponentenMap.set(node.id, {
                anlage: node.anlage,
                bezeichnung: node.label,
                typ: node.typ,
            });
            return {
                id: node.id,
                image: this.getSVG(node.id),
                shape: 'image',
                anlage: node.anlage,
                title: (title && `
                <div align = "center"> ${ de && de.long || title}
            <br>${ node.titel || ''} </div>`) || undefined,
            };

        }
        return {
            id: node.id,
            group: node.group,
            label: label,
            title: (title && `
            <div align = "center"> ${ de && de.long || title}
        <br>${ node.titel || ''} </div>`) || undefined,
        };
    }

    public getSVG(id: string, hoverColor?: string, focusColor?: string) {
        const appearance = this.config.appearance.graph.documents.KomponenteGA;
        const node = this.komponentenMap.get(id);
        const width = Math.max(
            this.getTextWidth(`GAK - ${node.typ}`, '55px Arial, Helvetica, sans-serif'),
            this.getTextWidth(node.bezeichnung, `${hoverColor || focusColor ? 'bold ' : ''}55px Arial, Helvetica, sans-serif`),
            this.getTextWidth(node.anlage, `´${Math.round(55 * 0.7)}px Arial, Helvetica, sans-serif`)
        ) + 75;
        const height = 210;
        return `${SVG_PREFIX}` +
            encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">` +
                `<rect x="5" y="5" width="${width - 10}" rx="20" ry="20" height="${height - 10}" ` +
                `style="fill:${hoverColor ? hoverColor : appearance.background};` +
                `stroke:${focusColor ? focusColor : appearance.border};stroke-width:5;"></rect>` +
                `<foreignObject x="5" y="5" rx="20" ry="20" width="${width - 10}" height="${height - 10}">` +
                `<div xmlns="http://www.w3.org/1999/xhtml" style="font-size:55px; ` +
                `color: ${appearance.fontcolor}; text-align: center; font-family: Arial, Helvetica, sans-serif;">` +
                `<span>GAK - ${node.typ}<br />` +
                `<span style="font-weight: ${hoverColor || focusColor ? 'bold' : 'normal'}">${node.bezeichnung}</span><br />` +
                `<span style="font-size: 70%;"> ${node.anlage} </span></span>` +
                `</div>` +
                `</foreignObject>` +
                `</svg>`);
    }

    public getTextWidth(text: string, font: string) {
        if (!this.canvas) {
            this.canvas = document.createElement('canvas');
        }
        const context = this.canvas.getContext('2d');
        context.font = font;
        const metrix = context.measureText(text);
        return Math.ceil(metrix.width);
    }

    public setSVG(node: string, hoverColor?: string, focusColor?: string) {
        const selected = this.graphData.nodes.get(
            {
                filter: (n) => n.id === node
            }
        );
        if (!selected.length) {
            return this.graphData;
        }
        this.graphData.nodes.update(
            {
                id: node,
                image: this.getSVG(node, hoverColor, focusColor),
            }
        );
    }

    /**
     * Generiert Edge Array für die Übergabe an vis.
     *
     * @param displayOptions Settings für die Visualisierung von Edges.
     * @returns vis.DataSet<vis.Edge>
     */
    public generateVisEdges(config?: any, displayOptions?: INavDisplayOptions): vis.DataSet<vis.Edge & { label?: string, title: string }> {

        // check
        if (!this._edgeGroups) {
            console.warn('Generierung der Nodes zur Visualisierung fehlgeschlagen: Keine Daten.');
            return null;
        }

        // initialize data
        const edges = new Array<vis.Edge & { label?: string, title: string }>();

        try {
            // nodeGroups
            for (const group of this._edgeGroups) {
                for (const child of group._children) {
                    if (!child.display) {
                        continue;
                    }

                    edges.push(this.generateVisEdge(child));
                }
            }
        } catch (err) {
            console.warn(err);
            if (this._messageSnackService) {
                this._messageSnackService.showSnackError(
                    'Fehler beim generieren der Beziehungen für den Graphen',
                    'NavSearchContainer');
            }
        }

        return new vis.DataSet(edges);
    }

    private generateVisEdge(edge: INavElementsEdge) {
        // displayOptions
        const edgeDisplayLabel = this.currentDisplayOpts ? this.currentDisplayOpts.displayEdgeLabels : true;

        const de = this.config.dictionary.find(entry => entry.key === edge.group);
        const label = (de && de.fwd) || (edge.group === 'default' ? 'ist Teil von' : '');
        const visEdge: vis.Edge & { label?: string, title: string } = {
            id: edge.id,
            from: edge.from,
            to: edge.to,
            label: (edgeDisplayLabel) ? label : undefined,
            title: edge.titel
        };
        if (this.config) {
            NavVisOptions.setEdgeConfig(edge.group, visEdge, this.config);
        }

        return visEdge;
    }

    // ---------------------------------------------------------------------------------------------
    // -- generatre tree component
    // ---------------------------------------------------------------------------------------------
    public generateTreeNodes(
        config: INavConfig
    ): any {

        // sanity
        if (!this._virtualRoot) {
            console.warn('Virtual root erwartet');
            return;
        }

        // virtual root
        const selectedNode = _.cloneDeep(this._virtualRoot.rootObj);

        // rootNode: z.B. Meldung
        let dictItem = _.find(config.dictionary, item => item.key === selectedNode.group);
        if (!dictItem) {
            // change search if root
            dictItem = _.find(config.dictionary, item => item.key === selectedNode.id.split('/')[0]);
            // label ist 'root' -> überschreiben mit Element id
            selectedNode.group = selectedNode.id.split('/')[0];
            if (!dictItem) {
                console.warn('Dictionary zur: ' + selectedNode.id + ' nicht vorhanden');
                return;
            }
        }
        let treeConfig: any;
        // MOCK, an dieser Stelle später die treeConfig aus der ClientConfig lesen
        if (selectedNode.group === 'Steuerung' || selectedNode.group === 'Funktionsgruppe') {
            treeConfig = [{
                root: 'Steuert',
                child: 'GehoertZu',
                replace: 'Schaltet',
                direction: 'bkwd',
                property: 'aktion',
                additional: [
                    {
                        for: 'KomponenteGA',
                        properties: {
                            typ: 'EVAK',
                        },
                        edge: 'IstIn',
                        collection: 'BereichEbene'
                    }
                ]
            }
            ];
        }
        return this.generateTreeNodesInner(config, selectedNode, dictItem, treeConfig);
    }

    private generateTreeNodesInner(
        config: INavConfig,
        selectedNode: INavElementsNode,
        dictItemRoot: any,
        treeConfig: any[] | undefined
    ): any {
        // to generate UID
        let __nodeIndex = 0;
        if (!treeConfig) {
            treeConfig = [];
        }

        // selectierter Node
        const outNodes: ITreeNode[] = [
            {
                id: __nodeIndex++,
                _id: selectedNode.id,                     // arangoID
                modeGroup: selectedNode.group,           // z.B. Meldung
                nodeGroupUi: dictItemRoot.long || selectedNode.group,
                nodeGroupAbbrUi: dictItemRoot.short,
                bezeichnung: selectedNode.label,
                isExpanded: true,
                type: NavListNodeType_root,               // 'root'
                children: []
            }
        ];

        try {
            // collect Subgraph ausgehend von selectierten Node
            const edgeGroups: NavElementsEdgeGroup[] = [];
            let dictItem1: any = null;

            // Level 1
            this.collectEdgesOfSubGraph(
                this._virtualRoot.rootObj.id,             // seed node
                edgeGroups,                             // output: edge groups
                navigationConfig.displayDefaultEdgeGroup  // filter: all
            );

            // Auszug aus config: edgeDefinition
            let edgeDefRoot: IEdgeDefinition = config.edgeDefinitions[selectedNode.group];
            if (!edgeDefRoot) {
                // change search if root node
                edgeDefRoot = config.edgeDefinitions[selectedNode.id.split('/')[0]];
                if (!edgeDefRoot) {
                    console.warn('EdgeDefinition: "' + selectedNode.group + '" nicht vorhanden');
                    return;
                }
            }

            const replace: string[] = treeConfig ? treeConfig.map(e => e.replace) : [];

            // Object für alle Objekte in edgeDefinition anlegen
            const edgeDepth1: ITreeEdge[] = [];

            for (const edgeDef in edgeDefRoot) {
                if (edgeDefRoot.hasOwnProperty(edgeDef)) {
                    dictItem1 = _.find(config.dictionary, item => item.key === edgeDef);

                    if (replace.find(e => e === edgeDef)) {
                        continue;
                    }
                    if (edgeDefRoot[edgeDef].to) {
                        edgeDepth1.push(
                            {
                                id: __nodeIndex++,
                                edgeGroupUi: dictItem1.fwd,
                                edgeGroup: edgeDef,
                                edgeDirection: EdgeDirectionFWD,
                                type: NavListNodeType_edgeLevel1,      // edgeLevel1
                                isExpanded: true,
                                children: []
                            });
                    }
                    if (edgeDefRoot[edgeDef].from) {
                        edgeDepth1.push(
                            {
                                id: __nodeIndex++,
                                edgeGroupUi: dictItem1.bkwd,
                                edgeGroup: edgeDef,
                                edgeDirection: EdgeDirectionBKWD,
                                type: NavListNodeType_edgeLevel1,      // edgeLevel1
                                isExpanded: true,
                                children: []
                            });
                    }
                }
            }

            const skipped = new Array<NavElementsEdgeGroup>();
            const map = new Map<string, any>();

            const emptyEdgegroups = replace.filter(entry => !edgeGroups.some(group => group.name === entry));
            for (const newGroup of emptyEdgegroups) {
                edgeGroups.push(new NavElementsEdgeGroup(newGroup));
            }

            // edge groups: z.B. Meldet
            for (const edgeGrp of edgeGroups) {

                if (replace.some(entry => entry === edgeGrp.name)) {
                    skipped.push(edgeGrp);
                    continue;
                }

                // edges - level 1 ------------------------------------------------
                for (const edge of edgeGrp._children) {

                    let edgeDef: ITreeEdge = null;
                    let nd: INavElementsNode = null;
                    let dict = null;

                    // default edges ausfiltern
                    if (edge.group.toUpperCase() === 'DEFAULT') {
                        // next
                        continue;
                    }
                    const [edgeDirection, property] = edge.from === selectedNode.id ?
                        [EdgeDirectionFWD, 'to'] : [EdgeDirectionBKWD, 'from'];

                    // hole edgeDef
                    edgeDef = edgeDepth1.find(o => o.edgeGroup === edge.group && o.edgeDirection === edgeDirection);
                    if (!edgeDef) {
                        continue;
                    }

                    // hole node aus containter
                    nd = this.findNodeWithID(edge[property]);
                    if (nd.group === 'root') { nd.group = nd.id.split('/')[0]; }
                    // hole dict für node
                    dict = _.find(config.dictionary, item => item.key === edge[property].split('/')[0]);
                    // add edge
                    const nodesDepth1: ITreeNode = {
                        id: __nodeIndex++,                     // list control id
                        _id: nd.id,                            // arangoID
                        nodeAbbr: dict.short,
                        bezeichnung: nd.label,
                        type: NavListNodeType_nodeLevel1,      // nodeLevel1
                        isExpanded: false,
                        children: []
                    };
                    edgeDef.children.push(nodesDepth1);

                    const edgeConfig = treeConfig.find(e => e.root === edge.group);
                    if (!edgeConfig) {
                        continue;
                    }
                    const [coll] = nd.id.split('/');
                    const edgedef = config.edgeDefinitions[coll][edgeConfig.child];
                    if (!edgedef || !edgedef.from && !edgedef.to) {
                        continue;
                    }

                    const edgeName = config.dictionary.find(item => item.key === edgeConfig.child);
                    const edgeDepth2: ITreeEdge = {
                        id: __nodeIndex++,
                        edgeGroupUi: edgeName[edgeConfig.direction],
                        edgeGroup: edgeConfig.child,
                        edgeDirection: EdgeDirectionFWD,
                        type: NavListNodeType_edgeLevel2,
                        isExpanded: true,
                        children: []
                    };
                    nodesDepth1.children.push(edgeDepth2);
                    map.set(nd.id, { edge: edgeConfig.child, edgeGroup: edgeDepth2 });
                }
            }

            for (const [key, value] of Array.from(map)) {
                const edgeGrps: NavElementsEdgeGroup[] = [];
                this.collectEdgesOfSubGraph(
                    key,             // seed node
                    edgeGrps,                             // output: edge groups
                    navigationConfig.displayDefaultEdgeGroup  // filter: all
                );
                const edgeConfig = treeConfig.find(entry => entry.child === value.edge);
                const skippedEdges = skipped.find(entry => entry.name === edgeConfig.replace);

                const edges = edgeGrps.find(entry => entry.name === value.edge);
                if (!edges) {
                    continue;
                }
                for (const edge of edges._children) {
                    const [edgeDirection, property] = edge.from === key ? [EdgeDirectionFWD, 'to'] : [EdgeDirectionBKWD, 'from'];
                    const nodeId = edge[property];
                    const collection = nodeId.split('/')[0];

                    // hole node aus containter
                    const nd = this.findNodeWithID(nodeId);
                    if (nd.group === 'root') { nd.group = nd.id.split('/')[0]; }
                    // hole dict für node
                    const dict = _.find(config.dictionary, item => item.key === collection);

                    let states;
                    let replacedEdge;
                    const subtype = config.appearance.subtype[collection];
                    let uiType = dict.short;
                    if (subtype) {
                        uiType = nd[subtype];

                        if (skippedEdges) {
                            replacedEdge = skippedEdges._children.find(entry => entry.from === nodeId || entry.to === nodeId);

                            states = [{
                                value: '-1',
                                uiMessage: '-undef-',
                            }];
                            const dicttype = config.dictionary.find(entry =>
                                entry.type === 'typeKey' && entry.collection === collection && entry.short === nd[subtype]);

                            for (const state in dicttype.aktion) {
                                if (dicttype.aktion.hasOwnProperty(state)) {
                                    states.push({
                                        value: state,
                                        uiMessage: dicttype.aktion[state]
                                    });
                                }
                            }
                        }
                    }

                    // add edge
                    const nodesDepth2: ITreeNode = {
                        id: __nodeIndex++,                     // list control id
                        _id: nd.id,                            // arangoID
                        nodeAbbr: uiType,
                        isExpanded: false,
                        bezeichnung: nd.label,
                        type: NavListNodeType_nodeLevel2,      // nodeLevel1
                        children: [],
                        edgeGroup: edgeConfig.replace,
                        schaltzustaende: states,
                        schaltzustandValue: (replacedEdge && replacedEdge.aktion != null && replacedEdge.aktion.toString()) || '-1',
                    };
                    value.edgeGroup.edgeDirection = edgeDirection;

                    // additional aus Config
                    if (edgeConfig.additional) {
                        const additionalConfig = edgeConfig.additional.find((entry) => entry.for === collection &&
                            (entry.properties && entry.properties.typ ? entry.properties.typ === uiType : true));
                        if (additionalConfig) {
                            const addEdges = this.getEdgesFromNode(nd);
                            const filteredEdge = addEdges && addEdges.find((addEdge) => addEdge.group === additionalConfig.edge
                                && (additionalConfig.collection === addEdge.from.split('/')[0]
                                    || additionalConfig.collection === addEdge.to.split('/')[0]));
                            if (filteredEdge) {
                                const direction = filteredEdge.from === nd.id ? 'to' : 'from';
                                const additionalNode = this.findNodeWithID(filteredEdge[direction]);
                                if (additionalNode) {
                                    const additionalDict = _.find(config.dictionary, item => item.key === additionalNode.group);

                                    nodesDepth2.additional = additionalNode.label;
                                    nodesDepth2.additionalAbbr = additionalDict && additionalDict.short;
                                }
                            }
                        }
                    }
                    // ---

                    value.edgeGroup.children.push(nodesDepth2);
                }
                value.edgeGroup.children = _.sortBy(value.edgeGroup.children, function (e) { return e.bezeichnung; });
            }

            // sort nodes und push to tree
            edgeDepth1.forEach(e => {
                const arr = _.sortBy(e.children, function (o) { return o.bezeichnung; });
                e.children = arr;
                outNodes[0].children.push(e);
            });

        } catch (err) {
            console.warn(err);
        }

        return outNodes;
    }

    /**
    * Liefert Edge die die Nodes (Parameter und virtualRoot) verbindet.
    *
    * @param nodeId: string
    * @returns INavElementsEdge Object oder null falls nicht gefunden
    */
    public findEdge(nodeId: string): INavElementsEdge {
        // virtual root
        const __selectedNode = this._virtualRoot.rootObj;

        // find edge
        const edge: INavElementsEdge = this.findEdgeWithNodeIDs(nodeId, __selectedNode.id);
        if (!edge) {
            return null;
        }

        // ok
        return edge;
    }

    /**
    * Löscht Edge die die Nodes (Parameter und virtualRoot) verbindet.
    *
    * @param edgeID: string
    * @returns boolean True wenn edge gelöscht wurde, sonst false
    */
    public removeEdgeToVirtualRoot(nodeID: string): boolean {
        // virtual root
        const __selectedNode = this._virtualRoot.rootObj;

        // find edge
        const edge: INavElementsEdge = this.findEdgeWithNodeIDs(nodeID, __selectedNode.id);
        if (!edge) {
            console.warn('Missing edge in container');
            return false;
        }

        // remove edge
        const __res = this.removeEdgeWithID(edge.id);
        if (!__res) {
            console.warn('Can not remove edge from container');
            return false;
        }

        // ok -> removed
        return true;
    }

    /**
    * Fügt neue Edge dem Model hinzu.
    *
    * @param edge: INavElementsEdge
    * @returns boolean True wenn edge hinzugefügt wurde, sonst false
    */
    public addEdge(edge: INavElementsEdge): boolean {

        let done = false;

        try {
            if (this._nodeGroups.some(grp => grp._children.some(child => child.id === edge.from))
                && this._nodeGroups.some(grp => grp._children.some(child => child.id === edge.to))) {
                let edgeGroup = this._edgeGroups.find(entry => entry.name === edge.group);
                if (!edgeGroup) {
                    edgeGroup = new NavElementsEdgeGroup(edge.group);
                    this._edgeGroups.push(edgeGroup);
                }
                edgeGroup.addChild(edge);

                this.graphData.edges.update(this.generateVisEdge(edge));
            }
            done = true;

            this.regenerateNodeDepth();
            // ok
            return done;

        } catch (err) {
            console.log(err);
            console.warn('Anomalie beim Hinzufügen einer neuen Edge');
        }

        // error
        return false;
    }

    /**
    * Fügt neuen Node dem Model hinzu.
    *
    * @param node: INavElementsNode
    * @returns boolean True wenn node hinzugefügt wurde, sonst false
    */
    public addNode(node: INavElementsNode): boolean {

        let done = false;

        try {
            let nodeGroup = this._nodeGroups.find(entry => entry.name === node.group);
            if (!nodeGroup) {
                nodeGroup = new NavElementsNodeGroup(node.group);
                this._nodeGroups.push(nodeGroup);
            }
            nodeGroup.addChild(node);
            done = true;

            this.graphData.nodes.update(this.generateVisNode(node));

            this.regenerateNodeDepth();

            // ok
            return done;

        } catch (err) {
            console.warn('Anomalie beim Hinzufügen eines neuen Node');
        }

        // error
        return false;
    }

    // ---------------------------------------------------------------------------------------------
    // -- display filter
    // ---------------------------------------------------------------------------------------------

    /**
     * Generiert Object mit Node Array und Edge Array für die Übergabe an vis.
     *
     * @param displayOptions Options zur konfigration der Node, Edge Generierung.
     * @returns void
     */
    public applyDisplayFilter(displayOptions: INavDisplayOptions): void {

        // check
        if (!this._edgeGroups || !this._nodeGroups) {
            console.warn('Keine Edge oder Node Daten.');
            return null;
        }

        // sanitise
        let edgeLevels = displayOptions.displayEdgeLevels;
        if (edgeLevels > navigationConfig.displayMaximumEdgeDepth) {   // z.B. 3

            const s = 'Fehler bei der Filterung des Graphen [NavSearchContainer]';
            if (this._messageSnackService) {
                this._messageSnackService.showSnackError(s);
                console.warn(s);
            } else {
                console.warn(s);
            }
            return;
        }

        // displayLevels === 0 -> show all
        if (edgeLevels === 0) {
            edgeLevels = navigationConfig.displayMaximumEdgeDepth;
            // this.setDisplayFlagsOfNodesEdges(true);
            // return;
        }

        try {
            const correspNodeIDs: string[] = [];

            // reset display flags
            this.setDisplayFlagsOfNodesEdges(false);
            for (const ngrp of this._nodeGroups) {
                for (const node of ngrp._children) {
                    if (this._nodeDepthMap.get(node.id) <= edgeLevels) {
                        node.display = true;
                    }
                }
            }
            for (const egrp of this._edgeGroups) {
                for (const edge of egrp._children) {
                    const eDepth = Math.max(this._nodeDepthMap.get(edge.from), this._nodeDepthMap.get(edge.to));
                    if (eDepth <= edgeLevels) {
                        edge.display = true;
                    }
                }
            }
        } catch (err) {
            if (this._messageSnackService) {
                this._messageSnackService.showSnackError(
                    'Fehler beim generieren der Beziehungen für den Graphen',
                    'NavSearchContainer');
                console.warn(err);
            } else {
                console.warn(err);
            }
        }
    }


    /**
    * Ermittelt Knoten mit gegebener ID.
    *
    * @param nodeID ID des gesuchten Knotens.
    * @returns [INavSearchNode]
    */
    public findNodeWithID(
        nodeID: string,
    ): INavElementsNode {
        let result: INavElementsNode = null;
        this._nodeGroups.forEach((__ng: NavElementsNodeGroup) => {
            __ng._children.forEach((__n: INavElementsNode) => {
                if (__n.id === nodeID) {
                    result = __n;
                }
            });
        });
        return result;
    }

    public updateNode(
        data: any,
        config: INavConfig
    ): boolean {
        const node = this.findNodeWithID(data._id);

        if (!node) {
            return false;
        }

        const childType = node.group;

        const subtypesConfig = config.appearance && config.appearance.subtype;
        const subType = subtypesConfig && subtypesConfig[childType];

        node.label = data.bezeichnung;
        node.titel = data.anmerkung;
        node.typ = data[subType];
        node.art = data.art;
        this.graphData.nodes.update(this.generateVisNode(node));
        return true;
    }

    // ---------------------------------------------------------------------------------------------
    // -- Privat -----------------------------------------------------------------------------------
    // ---------------------------------------------------------------------------------------------

    /**
    * Selektiert Nodes nach seedNodeID und displayOptions.
    * Füllt array mit correspondierenden Notes.
    * Das Node Array kann doppelte Einträge enthalten!
    *
    * @param seedNodeIDs[] IDs der Knotens zu denen Edges gesucht werden.
    * @param correspNodeIDs[] Array mit Nodes IDs die zu befüllen ist
    * @returns void
    */
    private findConnectedEdgesAndCorresNodes(
        seedNodeIDs: string[],
        correspNodeIDs: string[],
        displayOptions: INavDisplayOptions
    ): void {
        // index
        let i = 0;

        // nodeGroups
        this._edgeGroups.forEach((item: NavElementsEdgeGroup) => {

            if (displayOptions.displayEdgeGroups === navigationConfig.displayDefaultEdgeGroup ||
                displayOptions.displayEdgeGroups === item.name) {

                item._children.forEach((edge: INavElementsEdge) => {

                    // for each seedNodes
                    seedNodeIDs.forEach((nodeID: string) => {
                        if (edge.to === nodeID) {
                            edge.display = true;
                            // already there?
                            i = _.findIndex(correspNodeIDs, (obj: string) => obj === edge.from);
                            if (i < 0) {
                                correspNodeIDs.push(edge.from);
                            }
                        } else if (edge.from === nodeID) {
                            edge.display = true;
                            // already there?
                            i = _.findIndex(correspNodeIDs, (obj: string) => obj === edge.to);
                            if (i < 0) {
                                correspNodeIDs.push(edge.to);
                            }
                        }
                    });
                });
            }

        });
    }

    /**
    * Selektiert Edges und Nodes ausgehend von seedNodeID und abhängig vom edgeFilter.
    * Füllt array mit zusammenhängenden Edges.
    * Füllt array mit correspondierenden Notes.
    *
    * @param seedNodeIDs:string[] IDs der Knotens zu denen Edges gesucht werden.
    * @param edges:string[] Array mit Edges die zu befüllen ist
    * @param nodes:string[] Array mit Nodes IDs die zu befüllen ist
    * @param edgeFilter:string Filter mit Edge-Typ
    * @returns void
    */
    private collectEdgesOfSubGraph(
        seedNodeIDs: string,
        outEdgesGroups: NavElementsEdgeGroup[],
        edgeGroupFilter: string
    ): void {
        // index
        let i = 0;
        let eg: NavElementsEdgeGroup = null;

        // nodeGroups
        this._edgeGroups.forEach((edgeGrp: NavElementsEdgeGroup) => {

            if (edgeGroupFilter === navigationConfig.displayDefaultEdgeGroup ||
                edgeGroupFilter === edgeGrp.name) {

                edgeGrp._children.forEach((edge: INavElementsEdge) => {

                    if (edge.to === seedNodeIDs || edge.from === seedNodeIDs) {
                        // already there?
                        i = _.findIndex(outEdgesGroups, (obj: NavElementsEdgeGroup) => obj.name === edgeGrp.name);
                        if (i < 0) {
                            eg = new NavElementsEdgeGroup(edgeGrp.name);
                            outEdgesGroups.push(eg);
                        } else {
                            eg = outEdgesGroups[i];
                        }
                        // add edge
                        eg.addChild(_.cloneDeep(edge));
                    }
                });
            }

        });
    }

    /**
    * Setzt die display flags aller Nodes und Edges den übergebenen Wert.
    *
    * @returns void
    */
    private setDisplayFlagsOfNodesEdges(
        displayFlag: boolean
    ): void {
        // edges
        this._edgeGroups.forEach((item: NavElementsEdgeGroup) => {
            item._children.forEach((edge: INavElementsEdge) => {
                edge.display = displayFlag;
            });
        });
        // nodes
        this._nodeGroups.forEach((item: NavElementsNodeGroup) => {
            item._children.forEach((node: INavElementsNode) => {
                node.display = displayFlag;
            });
        });
    }

    /**
     * Ermittelt Edge mit gegebenen nodeIDs.
     *
     * @param nodeID IDs des Knoten der Edge.
     * @returns [INavSearchNode]
     */
    private findEdgeWithNodeIDs(
        nodeID1: string,
        nodeID2: string
    ): INavElementsEdge {
        let result: INavElementsEdge = null;
        this._edgeGroups.forEach((__eg: NavElementsEdgeGroup) => {
            __eg._children.forEach((__e: INavElementsEdge) => {

                if (__e.from === nodeID1 || __e.to === nodeID1) {
                    if (__e.from === nodeID2 || __e.to === nodeID2) {
                        result = __e;
                    }
                }

            });
        });
        return result;
    }

    public getEdgesFromNode(node: INavElementsNode): INavElementsEdge[] {
        return _.filter(
            _.flatMap(this._edgeGroups, (groups) => groups._children),
            (edge) => edge.from === node.id || edge.to === node.id
        );
    }

    /**
     * Löscht Edge mit gegebener nodeID.
     *
     * @param edgeID ID der Edge.
     * @returns [INavSearchNode]
     */
    public removeEdgeWithID(
        edgeID: string
    ): boolean {
        const edgeGroup = this._edgeGroups.find(eg => eg._children.some(child => child.id === edgeID));

        // sanity
        if (!edgeGroup) {
            console.warn('Edge Gruppe nicht ermittelbar');
            return false;
        }

        const edge = edgeGroup._children.find(child => child.id === edgeID);
        edgeGroup._children = edgeGroup._children.filter(child => child.id !== edgeID);
        this.graphData.edges.remove(edgeID);
        // Entferne Nodes ohne weitere Edges
        for (const nodeID of [edge.from, edge.to]) {
            const edges = this.graphData.edges.get({ filter: (eg) => eg.from === nodeID || eg.to === nodeID });
            if (!edges || !edges.length) {
                this.graphData.nodes.remove(nodeID);
            }
        }

        this.regenerateNodeDepth();
        // ok
        return true;
    }

    removeNodeWithID(nodeID: string): boolean {
        let __ngToShrink: NavElementsNodeGroup = null;
        let __nTemp: INavElementsNode[] = null;

        this._nodeGroups.forEach((__ng: NavElementsNodeGroup) => {
            __ng._children.forEach((__n: INavElementsNode) => {

                if (__n.id === nodeID) {
                    __ngToShrink = __ng;
                }

            });
        });

        // sanity
        if (!__ngToShrink) {
            console.warn('Node Gruppe nicht ermittelbar');
            return false;
        }

        // reduce array with edges
        __nTemp = _.remove(__ngToShrink._children, (obj: INavElementsEdge) => obj.id !== nodeID);
        __ngToShrink._children = __nTemp;
        this.graphData.nodes.remove(nodeID);

        // ok
        return true;
    }

    /**
     * Set das root und das virtual_root Object.
     *
     * @param [root: NavSearchRoot] Object vom Type NavSearchRoot
     * @returns void
     */
    private setRootAndVirtualRoot(node: INavElementsNode): void {
        this._root = new NavElementsRoot(node);
        this._virtualRoot = new NavElementsRoot(node);
    }

    /**
     * Set das virtual_root Object.
     *
     * @param [root: NavSearchRoot] Object vom Type NavSearchRoot
     * @returns void
     */
    private setVirtualRoot(node: INavElementsNode): void {
        this._virtualRoot = new NavElementsRoot(node);
        if (node.group !== navigationConfig.loadRespondDataVirtualRootIdentifier) {
            this.regenerateNodeDepth();
        }
    }

    /**
     * Prüft Existenz der NodeGroup, fügt NodeGroup ggf. der Collection hinzu.
     *
     * @param [childGroupName: string] Name der NodeGroup
     * @returns NavSearchChildGroup
     */
    public addNodeGroup(childGroupName: string): NavElementsNodeGroup {
        let group: NavElementsNodeGroup = null;

        this._nodeGroups.forEach((item: NavElementsNodeGroup) => {
            if (item.name === childGroupName) {
                group = item;
                return;
            }
        });

        if (!group) {
            group = new NavElementsNodeGroup(childGroupName);
            this._nodeGroups.push(group);
        }
        return group;
    }

    /**
     * Prüft Existenz der EdgeGroup, fügt EdgeGroup ggf. der Collection hinzu.
     *
     * @param [childGroupName: string] Name der EdgeGroup
     * @returns NavSearchEdgeGroup
     */
    public addEdgeGroup(childGroupName: string): NavElementsEdgeGroup {
        let group: NavElementsEdgeGroup = null;

        this._edgeGroups.forEach((item: NavElementsEdgeGroup) => {
            if (item.name === childGroupName) {
                group = item;
                return;
            }
        });

        if (!group) {
            group = new NavElementsEdgeGroup(childGroupName);
            this._edgeGroups.push(group);
        }
        return group;
    }

    /**
     * Schreibt alle Elemente auf die Console.
     *
     * @returns void
     */
    private dumpContent(): void {
        // root
        if (this._root) {
            console.log('[root]');
            console.log(this._root.rootObj);
        }
        if (this._virtualRoot) {
            console.log('[virtual root]');
            console.log(this._virtualRoot.rootObj);
        }

        // nodeGroups
        if (this._nodeGroups) {
            this._nodeGroups.forEach((item: NavElementsNodeGroup) => {
                console.log('[nodeType] -- ' + item.name + '--------');
                item._children.forEach((child: INavElementsNode) => {
                    console.log('  ', child);
                });
            });
        }

        // edgeGroup
        if (this._edgeGroups) {
            this._edgeGroups.forEach((item: NavElementsEdgeGroup) => {
                console.log('[edgeType] -- ' + item.name + '--------');
                item._children.forEach((child: INavElementsEdge) => {
                    console.log('  ', child);
                });
            });
        }
    }

    // ---------------------------------------------------------------------------------------------
    // -- Parse SearchResult -----------------------------------------------------------------------
    // ---------------------------------------------------------------------------------------------

    private parseRoot(root: any, config: INavConfig) {
        const [grp] = (root._id as string).split('/');
        const subType = this.getSubtype(grp, config);
        const rootNode = {
            id: root._id,
            typ: root[subType],
            group: grp,
            label: root.bezeichnung,
            titel: root.anmerkung,
            isExpanded: true,
            anlage: root.anlage,
        };
        // create root object
        this.setRootAndVirtualRoot(rootNode);

        // create node child object
        this.addNodeGroup(grp).addChild(rootNode);
    }

    private parseResult(result: any, config: INavConfig) {
        // create root object
        this.setVirtualRoot(
            {
                id: navigationConfig.defaultValuesForRoot.id,
                group: navigationConfig.loadRespondDataVirtualRootIdentifier,
                label: navigationConfig.defaultValuesForRoot.bezeichnung,
                titel: navigationConfig.defaultValuesForRoot.anmerkung
            }
        );

        // create node child object
        this.addNodeGroup(navigationConfig.loadRespondDataRootIdentifier).addChild(
            {
                id: navigationConfig.defaultValuesForRoot.id,
                group: navigationConfig.loadRespondDataRootIdentifier,
                label: navigationConfig.defaultValuesForRoot.bezeichnung,
                titel: navigationConfig.defaultValuesForRoot.anmerkung
            }
        );

        // take property array and iterate
        const childs: any = result;
        childs.forEach(child => {
            const group = child._id.split('/')[0] || 'default';
            const subType = this.getSubtype(group, config);
            // create node child object
            this.addNodeGroup(group).addChild(
                {
                    id: child._id,
                    group: group,
                    label: child.bezeichnung,
                    titel: child.anmerkung,
                    typ: child[subType],
                    display: true,
                    anlage: child.anlage,
                }
            );

            // create edge from node to root
            this.addEdgeGroup('default').addChild(
                {
                    key: child._key,
                    rev: child._rev,
                    id: child._id,     // identical to node id
                    from: child._id,
                    to: navigationConfig.defaultValuesForRoot.id,    // node root id
                    group: 'default',
                    label: 'ist Teil von',
                    display: true
                }
            );
        });
    }

    public addNodeToGraph(node: IDbDoc, config: INavConfig): void {
        const [collection] = node._id.split('/');

        const childGroup = this.addNodeGroup(collection);
        const subType = this.getSubtype(collection, config);

        const navElem = {
            id: node._id,
            group: collection,
            label: node.bezeichnung,
            titel: node.anmerkung,
            typ: node[subType],
            art: node['art'],
            display: true,
            anlage: node['anlage'],
        };
        childGroup.addChild(navElem);
        this.graphData.nodes.update(this.generateVisNode(navElem));
    }

    private parseExtendRoot(root: any, config: INavConfig) {
        // sanity - check if extendedResult provide requested ID
        // nur wenn HTTP calls nicht gemockt werden
        if (
            !appConfig.dev.mockingMode &&
            root._id !== this._rootToExtend.rootObj.id
        ) {
            console.warn('Extended result: root ID do not match requested ID');
            return false;
        }

        const [grp] = (root._id as string).split('/');
        const subType = this.getSubtype(grp, config);
        const rootNode = {
            id: root._id,
            group: grp,
            label: root.bezeichnung,
            titel: root.anmerkung,
            typ: root[subType],
            display: true,
            isExpanded: true,
            anlage: root['anlage'],
        };
        // set virtual root object
        this.setVirtualRoot(rootNode);

        // create node child object
        this.addNodeGroup(grp).addChild(rootNode);
    }

    private parseChilds(childObj: any, config: INavConfig) {
        /* tslint:disable */
        for (const childType in childObj) {
            const childs = childObj[childType];
            const childGroup = this.addNodeGroup(childType);
            const subType = this.getSubtype(childType, config);
            const nodes = new Array<INode>();
            for (const child of childs) {
                // create node child object
                const node = {
                    id: child._id,
                    group: childType,
                    label: child.bezeichnung,
                    titel: child.anmerkung,
                    typ: child[subType],
                    art: child.art,
                    display: true,
                    anlage: child['anlage'],
                }
                // Hinweis: addChild prüft auf Existenz
                childGroup.addChild(node);
                nodes.push(this.generateVisNode(node));
            }
            this.graphData.nodes.update(nodes);
        }
        /* tslint:enable */
    }

    public getSubtype(group: string, config: INavConfig): string | null {
        return config.appearance.subtype[group];
    }

    private parseEdges(edgeObj: any) {
        /* tslint:disable */
        for (const edgeType in edgeObj) {
            if (!edgeType) {
                continue;
            }
            const edges: any = edgeObj[edgeType];
            const edgeGroup = this.addEdgeGroup(edgeType);
            const edgeArray = new Array<IEdge>();
            for (const edge of edges) {
                // create node child object
                const navEdge = {
                    key: edge._key,
                    rev: edge._rev,
                    id: edge._id,
                    from: edge._from,
                    to: edge._to,
                    group: edgeType,
                    label: edgeType,
                    aktion: edge.aktion,
                    display: true
                }
                // Hinweis: addChild prüft auf Existenz
                edgeGroup.addChild(navEdge);
                edgeArray.push(this.generateVisEdge(navEdge));
            }
            this.graphData.edges.update(edgeArray);
        }
        /* tslint:enable */
    }

    private regenerateNodeDepth() {
        this._nodeDepthMap = new Map<string, number>();
        const adj = new Map<string, Array<string>>();
        for (const ngrp of this._nodeGroups) {
            for (const node of ngrp._children) {
                adj.set(node.id, new Array<string>());
            }
        }
        for (const egrp of this._edgeGroups) {
            for (const edge of egrp._children) {
                adj.get(edge.from).push(edge.to);
                adj.get(edge.to).push(edge.from);
            }
        }

        const edgeQueue = new Array<string>();
        const visited = new Set<string>();
        edgeQueue.push(this._virtualRoot.rootObj.id);
        this._nodeDepthMap.set(this._virtualRoot.rootObj.id, 0);
        while (edgeQueue.length) {
            const nodeId = edgeQueue.shift();
            if (visited.has(nodeId)) {
                continue;
            }
            visited.add(nodeId);
            const nextDepth = this._nodeDepthMap.get(nodeId) + 1;
            for (const nextNode of adj.get(nodeId)) {
                if (!this._nodeDepthMap.has(nextNode)) {
                    edgeQueue.push(nextNode);
                    this._nodeDepthMap.set(nextNode, nextDepth);
                }
            }
        }
    }

    /**
     * Parsed den Inhalt des Parameters und generiert das Datenmodell im Container.
     *
     * @param [searchResult: any] Object das geparset werden soll.
     * @param [debug_output: boolean] Falls true, werden Control Ausgaben auf die Console geschrieben.
     * @returns Die Funktion gibt true im erfolgsfall und false im fehlerfall zurück.
     */
    private parseSearchResult(
        searchResult: any,
        config: INavConfig,
        debug_output: boolean = navigationConfig.debugModeNavSearchParse,
    ): boolean {

        // JS object
        if (!searchResult || typeof searchResult !== 'object') {
            console.warn('Übergebener Parameter ist null oder kein Object');
            return false;
        }

        const resultElem = searchResult[navigationConfig.loadRespondDataVirtualRootIdentifier];
        if (resultElem) {
            this.parseResult(resultElem, config);
            this.regenerateNodeDepth();
            return true;
        }

        const rootElem = searchResult[navigationConfig.loadRespondDataRootIdentifier];
        if (rootElem) {
            const childElem = searchResult['childs'];
            const edgeElem = searchResult['edges'];
            const rootCol = rootElem._id.split('/')[0];
            if (childElem[rootCol]) {
                childElem[rootCol].push(rootElem);
            } else {
                childElem[rootCol] = [rootElem];
            }
            if (this._rootToExtend) {
                this.parseExtendRoot(rootElem, config);
            } else {
                this.parseRoot(rootElem, config);
            }

            this.parseChilds(childElem, config);
            this.parseEdges(edgeElem);
            this.regenerateNodeDepth();
            return true;
        }

        return false;
    }

}

export interface IGraphData extends vis.Data {
    nodes: vis.DataSet<INode>;
    edges: vis.DataSet<IEdge>;
}
export type INode = vis.Node & vis.NodeOptions;
export type IEdge = vis.Edge & vis.EdgeOptions;



// WEBPACK FOOTER //
// /builds/cp/frap/t3/planer/src/app/nav/model/nav-elements-container.ts